import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchApi,
  createUser,
  resetUserPassword,
  deleteUser,
  fetchSurveyConfig,
  updateSurveyConfig,
  fetchParticipantSurveySchedule,
  updateParticipantSurveySchedule,
  fetchParticipantDiagnosticConfig,
  fetchParticipantDiagnosticLogs,
  fetchPublicApi,
  updateParticipantDiagnosticConfig,
} from "./client";
import type {
  AdminUser,
  DiagnosticDurationHours,
  DiagnosticLogFilters,
  DashboardSummary,
  Device,
  Participant,
  SensorFilters,
  SensorRecord,
  ParticipantSurveyScheduleUpdate,
  PublicStats,
  SurveyConfigUpdate,
  StudyHealth,
} from "./types";

export function usePublicStats() {
  return useQuery({
    queryKey: ["public", "stats"],
    queryFn: () => fetchPublicApi<PublicStats>("/public/stats"),
    staleTime: 60_000,
  });
}

export function useDashboardSummary() {
  return useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: () => fetchApi<DashboardSummary>("/dashboard/summary"),
  });
}

export function useStudyHealth(from: string, to: string) {
  return useQuery({
    queryKey: ["study-health", from, to],
    queryFn: () => fetchApi<StudyHealth>("/study-health", { from, to }),
  });
}

export function useParticipants(options?: { monitorAlerts?: boolean }) {
  const monitorAlerts = options?.monitorAlerts ?? false;
  return useQuery({
    queryKey: ["participants"],
    queryFn: () => fetchApi<Participant[]>("/participants"),
    refetchInterval: monitorAlerts ? 5 * 60 * 1000 : false,
    refetchIntervalInBackground: monitorAlerts,
  });
}

export function useParticipantSensors(
  userId: string,
  filters?: SensorFilters
) {
  return useQuery({
    queryKey: ["participants", userId, "sensors", filters],
    queryFn: () =>
      fetchApi<SensorRecord[]>(`/participants/${userId}/sensors`, {
        source: filters?.source,
        from: filters?.from,
        to: filters?.to,
      }),
    enabled: !!userId,
  });
}

export function useSensors(filters?: SensorFilters) {
  return useQuery({
    queryKey: ["sensors", filters],
    queryFn: () =>
      fetchApi<SensorRecord[]>("/sensors", {
        source: filters?.source,
        user_id: filters?.user_id,
        device_id: filters?.device_id,
        from: filters?.from,
        to: filters?.to,
        limit: filters?.limit ? String(filters.limit) : undefined,
        cursor: filters?.cursor,
      }),
    placeholderData: (prev) => prev,
  });
}

export function useSensorsCount(filters?: SensorFilters) {
  return useQuery({
    queryKey: ["sensors", "count", {
      source: filters?.source,
      user_id: filters?.user_id,
      device_id: filters?.device_id,
      from: filters?.from,
      to: filters?.to,
    }],
    queryFn: async () => {
      const res = await fetchApi<unknown>("/sensors/count", {
        source: filters?.source,
        user_id: filters?.user_id,
        device_id: filters?.device_id,
        from: filters?.from,
        to: filters?.to,
      });
      return res.count;
    },
  });
}

export function useSensorSources() {
  return useQuery({
    queryKey: ["sensors", "sources"],
    queryFn: () => fetchApi<string[]>("/sensors/sources"),
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["participants"] });
    },
  });
}

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => fetchApi<AdminUser[]>("/admin/users"),
  });
}

export function useDevices() {
  return useQuery({
    queryKey: ["admin", "devices"],
    queryFn: () => fetchApi<Device[]>("/admin/devices"),
  });
}

export function useUserDevices(userId: string) {
  return useQuery({
    queryKey: ["participants", userId, "devices"],
    queryFn: () => fetchApi<Device[]>(`/participants/${userId}/devices`),
    enabled: !!userId,
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (username: string) => resetUserPassword(username),
  });
}

export function useDeleteUser() {
  return useMutation({
    mutationFn: (username: string) => deleteUser(username),
  });
}

export function useSurveyConfig() {
  return useQuery({
    queryKey: ["admin", "survey-config"],
    queryFn: fetchSurveyConfig,
  });
}

export function useUpdateSurveyConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (config: SurveyConfigUpdate) => updateSurveyConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "survey-config"] });
      queryClient.invalidateQueries({ queryKey: ["participants"] });
    },
  });
}
export function useParticipantSurveySchedule(userId: string, enabled = true) {
  return useQuery({
    queryKey: ["participants", userId, "survey-schedule"],
    queryFn: () => fetchParticipantSurveySchedule(userId),
    enabled: enabled && !!userId,
  });
}

export function useUpdateParticipantSurveySchedule(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (schedule: ParticipantSurveyScheduleUpdate) =>
      updateParticipantSurveySchedule(userId, schedule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["participants", userId, "survey-schedule"] });
    },
  });
}

export function useParticipantDiagnosticConfig(userId: string, enabled = true) {
  return useQuery({
    queryKey: ["participants", userId, "diagnostic-config"],
    queryFn: () => fetchParticipantDiagnosticConfig(userId),
    enabled: enabled && !!userId,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

export function useParticipantDiagnosticLogs(
  userId: string,
  filters: DiagnosticLogFilters,
  enabled = true,
) {
  return useQuery({
    queryKey: ["participants", userId, "diagnostic-logs", filters],
    queryFn: () => fetchParticipantDiagnosticLogs(userId, filters),
    enabled: enabled && !!userId,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

export function useUpdateParticipantDiagnosticConfig(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (config: { enabled: boolean; duration_hours: DiagnosticDurationHours }) =>
      updateParticipantDiagnosticConfig(userId, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["participants", userId, "diagnostic-config"] });
      queryClient.invalidateQueries({ queryKey: ["participants", userId, "diagnostic-logs"] });
    },
  });
}
